'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { generateCompanySlug } from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import type { FilterParams } from '@/lib/search-filter-params';

/** The user's last keyword searches (persisted server-side per user). */
function useLastSearches() {
  // Last searches state
  const [lastSearches, setLastSearches] = useState<string[]>([]);

  const fetchLastSearches = useCallback(async () => {
    try {
      const res = await fetch('/api/search/last-searches');
      if (res.ok) {
        const data = await res.json();
        const keywords = data.keywords ?? [];
        setLastSearches(keywords);
        return keywords;
      }
    } catch {
      // silently ignore
    }
    return [];
  }, []);

  const saveLastSearch = useCallback(async (keyword: string) => {
    try {
      await fetch('/api/search/last-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword }),
      });
    } catch {
      // silently ignore
    }
  }, []);
  return { lastSearches, setLastSearches, fetchLastSearches, saveLastSearch };
}

/** Keyboard highlight within the autocomplete / last-searches dropdown. */
function useSuggestionHighlight(
  suggestions: CompanySearchResult[],
  showSuggestions: boolean,
  showLastSearches: boolean,
) {
  const [sidebarActiveIndex, setSidebarActiveIndex] = useState(-1);
  // True only while sidebarActiveIndex was set via ArrowUp/ArrowDown. Enter
  // honors a highlighted row only after keyboard navigation — a dropdown that
  // opens under the mouse cursor fires onMouseEnter immediately, and Enter
  // would otherwise re-run the hovered "last search" instead of the input
  // text (same fix as LandingSearch).
  const keyboardNavRef = useRef(false);

  // Reset active index when suggestions or dropdown visibility change
  useEffect(() => {
    setSidebarActiveIndex(-1); // eslint-disable-line react-hooks/set-state-in-effect -- highlight resets when the list it indexes changes (pre-existing; a try/finally in the old component hid it from the compiler)
    keyboardNavRef.current = false;
  }, [suggestions, showSuggestions, showLastSearches]);

  return { sidebarActiveIndex, setSidebarActiveIndex, keyboardNavRef };
}

/**
 * The top-level keyword input: company autocomplete, the last-searches
 * dropdown, and the submit that closes both and hands the staged filters on.
 */
export function useSidebarKeywordSearch(
  onSubmit: (filters: FilterParams) => void,
  collectFilters: () => FilterParams,
) {
  const router = useRouter();
  const { locale } = useLocale();

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<CompanySearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { lastSearches, setLastSearches, fetchLastSearches, saveLastSearch } = useLastSearches();
  const [showLastSearches, setShowLastSearches] = useState(false);

  const discardLastSearch = useCallback(
    async (keyword: string) => {
      setLastSearches((prev) => prev.filter((k) => k !== keyword));
      try {
        await fetch('/api/search/last-searches', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keyword }),
        });
        const refreshed = await fetchLastSearches();
        if (refreshed.length === 0) setShowLastSearches(false);
      } catch {
        // silently ignore
      }
    },
    // setLastSearches is stable (useState); listed only because it now
    // arrives from useLastSearches.
    [fetchLastSearches, setLastSearches],
  );

  // Fetch autocomplete suggestions via lightweight API route (same fast backend)
  const fetchSuggestions = useCallback((query: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setSuggestionsLoading(true);
      setShowSuggestions(true);
      try {
        const res = await fetch(
          `/api/companies/suggest?q=${encodeURIComponent(query)}&limit=5`,
          { signal: controller.signal },
        );
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setSuggestions(data.results || []);
      } catch (err: unknown) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          setSuggestions([]);
        }
      } finally {
        setSuggestionsLoading(false);
      }
    }, 200);
  }, []);

  const { sidebarActiveIndex, setSidebarActiveIndex, keyboardNavRef } =
    useSuggestionHighlight(suggestions, showSuggestions, showLastSearches);

  // Scroll active item into view
  useEffect(() => {
    if (sidebarActiveIndex >= 0 && autocompleteRef.current) {
      const activeEl = autocompleteRef.current.querySelector(
        '[data-active="true"]',
      );
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [sidebarActiveIndex]);

  // Close autocomplete / last searches on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        setShowLastSearches(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFormSubmit = useCallback(() => {
    setShowSuggestions(false);
    setShowLastSearches(false);
    const filters = collectFilters();
    if (filters.q.trim()) {
      saveLastSearch(filters.q.trim());
    }
    onSubmit(filters);
  }, [onSubmit, collectFilters, saveLastSearch]);

  const handleSuggestionClick = useCallback(
    (suggestion: CompanySearchResult) => {
      setShowSuggestions(false);
      setShowLastSearches(false);
      if (suggestion.name?.trim()) {
        saveLastSearch(suggestion.name.trim());
      }
      const slug = generateCompanySlug(
        {
          name: suggestion.name,
          sitz: suggestion.city,
          registerCourt: suggestion.registerCourt,
          registerType: suggestion.registerType,
          registerId: suggestion.registerId,
        },
        locale,
      );
      router.push(slug);
    },
    [router, saveLastSearch, locale],
  );

  return {
    autocompleteRef, suggestions, showSuggestions, setShowSuggestions, suggestionsLoading,
    sidebarActiveIndex, setSidebarActiveIndex, keyboardNavRef, lastSearches, showLastSearches,
    setShowLastSearches, fetchLastSearches, discardLastSearch, fetchSuggestions,
    handleFormSubmit, handleSuggestionClick,
  };
}
